import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  250: '#EAECF0',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  550: '#6373811F',
  600: '#637381',
  700: '#454F5B',
  750: '#37424B',
  760: '#333E49',
  800: '#212B36',
  850: '#19222B',
  900: '#161C24',
  950: '#1F242C',
};

const PRIMARY = {
  lighter: '#82cabf',
  light: '#2ea593',
  medium: '#117864',
  main: '#0f7865',
  dark: '#0b6856',
  darker: '#064c3b',
  darkest: '#1a3f3e',
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#D8FBDE',
  light: '#86E8AB',
  main: '#36B37E',
  main2: '#0F7864',
  status: '#38CA4F',
  dark: '#1B806A',
  darker: '#0A5554',
  newDark: '#26373d',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  bg: '#FDD4652B',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  bg: '#FF563036',
  contrastText: '#fff',
};

const MEMBERS_AREA = {
  access: {
    lifetime: '#3DD598',
    monthly: '#F6C445',
    yearly: '#4F8CFF',
    semiannual: '#9C5CFF',
    quarterly: '#FF8A34',
  },
};

const PRODUCT_TYPE_BADGE = {
  unique: {
    main: '#38CA4F',
    background: alpha('#38CA4F', 0.1),
  },
  subscription: {
    main: '#FFAB00',
    background: alpha('#FFAB00', 0.1),
  },
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  membersArea: MEMBERS_AREA,
  productTypeBadge: PRODUCT_TYPE_BADGE,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

// Superfícies do dark mode, neutras (sem tom azulado) e mais pretas. Ajustáveis a gosto.
// Substituem os cinzas azulados do template MUI APENAS no modo dark (o light nao muda).
const DARK_NEUTRAL = {
  760: '#2C2C2C', // background.muted   (era GREY[760] #333E49)
  800: '#1A1A1A', // background.paper / cards (era GREY[800] #212B36)
  850: '#141414', // background.dark    (era GREY[850] #19222B)
  900: '#0E0E0E', // background.default = shell do app, "mais preto" (era GREY[900] #161C24)
  950: '#1C1C1C', // (era GREY[950] #1F242C)
};

export default function palette(themeMode) {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
      warning: WARNING.main,
    },
    background: { paper: '#fff', default: GREY[200], neutral: GREY[200], muted: GREY[250], hover: GREY[400], dark: GREY[200] },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
    border: {
      default: alpha(GREY[500], 0.32)
    },
    grey: {
      ...GREY,
      550: GREY[300],
      600: GREY[400],
      700: GREY[400],
      950: GREY[200],
    },
    primary: {
      ...PRIMARY,
      darkest: alpha(PRIMARY.light, 0.12),
      medium: PRIMARY.dark,
    }
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: GREY[500],
      disabled: GREY[600],
      warning: WARNING.main,
    },
    background: {
      paper: DARK_NEUTRAL[800],
      default: DARK_NEUTRAL[900],
      neutral: alpha(GREY[500], 0.16),
      muted: DARK_NEUTRAL[760],
      hover: GREY[600],
      dark: DARK_NEUTRAL[850]
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
    border: {
      default: alpha(GREY[500], 0.32)
    },
    grey: {
      ...GREY,
      760: DARK_NEUTRAL[760],
      800: DARK_NEUTRAL[800],
      850: DARK_NEUTRAL[850],
      900: DARK_NEUTRAL[900],
      950: DARK_NEUTRAL[950],
    }
  };

  return themeMode === 'light' ? light : dark;
}
